import { useCallback, useEffect, useRef, useState } from "react";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { useDebouncedCallback } from "@me8eon/debounce";
import {
    fetchKpis,
    fetchCategories,
    fetchSentiment,
    fetchCallbackRequests,
    fetchLiveChatRequests,
    fetchIncidents,
    fetchIncidentCategories,
    fetchUniqueUsers,
    DashboardRange,
    DashboardKpis,
    RequestBreakdown,
    DashboardIncidents,
    LiveIncident,
    IncidentCategory,
    DashboardUniqueUsers,
} from "../../services/dashboardApi";
import { KPIS, CategoryRow, SentimentRow, SelectedCategory } from "./poData";

const EMPTY_REQUESTS: RequestBreakdown = { window: 0, total: 0, byLanguage: [], languages: [] };

// Selecting several categories/companies from a multi-select fires one state change PER click —
// debounce the actual network round-trip so a burst of clicks collapses into one request.
const FILTER_DEBOUNCE_MS = 400;

// SAFETY: never show mock/fake numbers on a live dashboard. When data hasn't loaded (or a
// fetch failed) we fall back to honest ZEROS + empty lists, and surface an `error` flag so the
// tabs can show a clear "couldn't load" message instead of inventing traffic.
const ZERO_KPIS: typeof KPIS = {
    totalQueries: 0,
    totalQueriesDelta: 0,
    categories: 0,
    incidentsRaised: 0,
    incidentsDelta: 0,
    incidentsOpen: 0,
    incidentsInProgress: 0,
    incidentsResolved: 0,
    sentimentPositivePct: 0,
    likes: 0,
    dislikes: 0,
};

// KPIs the tabs render. Deltas are null when there's no valid comparison (single-category filter,
// or thin history). Incident fields are now live (from the Snowflake incident feed).
export type KpisView = Omit<typeof KPIS, "totalQueriesDelta" | "incidentsDelta"> & {
    totalQueriesDelta: number | null;
    incidentsDelta: number | null;
};

export type DashboardData = {
    kpis: KpisView;
    categories: CategoryRow[];
    sentiments: SentimentRow[];
    // Window + category/company filtered server-side, same as the rest of this shape.
    callbackRequests: RequestBreakdown;
    liveChatRequests: RequestBreakdown;
    incidents: LiveIncident[]; // window + category/company filtered server-side (carry parent/child)
    incidentCategories: IncidentCategory[]; // incidents grouped by category (breakdown table)
    uniqueUsers: DashboardUniqueUsers | null; // Overview's per-day/week/month charts — not window-scoped
    loading: boolean;
    error: boolean; // true when the core data failed to load — tabs show an error, never fake data
};

/** Derive the incident card KPIs from a list of incidents (+ prior-window count for the delta).
 *  Both are already scoped server-side to the active category/company filters. */
function deriveIncidentKpis(items: LiveIncident[], prevTotal: number, priorComplete: boolean) {
    const raised = items.length;
    return {
        incidentsRaised: raised,
        incidentsOpen: items.filter((i) => i.status === "Open").length,
        incidentsInProgress: items.filter((i) => i.status === "In Progress").length,
        incidentsResolved: items.filter((i) => i.status === "Resolved").length,
        // delta only once we hold a COMPLETE, non-empty prior window (else no honest comparison)
        incidentsDelta: !priorComplete || !prevTotal ? null : Math.round(((raised - prevTotal) / prevTotal) * 100),
    };
}

/**
 * Fetches KPIs / categories / sentiment / incidents / contact-request cards for the selected
 * window PLUS the active category (taxonomy child ids) and company filters — all sent straight
 * to the backend, which does the actual filtering (and, for KPIs/incidents, the prior-window
 * delta) server-side. The one thing still done client-side: when categories are selected, backfill
 * a zero row for any selected category that has no matching data in the window (groupBy naturally
 * omits empty groups, but the UI should still list every selected category).
 *
 * The actual network round-trip is debounced (FILTER_DEBOUNCE_MS) for every filter/range change,
 * INCLUDING while a category/company multi-select menu is open — a multi-select fires one state
 * change per click, so without this a burst of clicks would fire one full fetch per click; the
 * debounce collapses that into a single request, whether or not the dropdown is still open.
 * Auth readiness is handled by a separate, non-debounced effect that fires the very first fetch
 * immediately once the user is actually logged in; `loggedIn` is deliberately never part of the
 * debounced effect's own deps, so auth state can't reset or delay it. (idToken itself is a fn —
 * always truthy — so it can't signal readiness; see UserData.loggedIn.)
 */
export function useDashboardData(range: DashboardRange, categoryFilter: SelectedCategory[], companyFilter: string[] = []): DashboardData {
    const idToken = useJwtToken();
    const { loggedIn } = useUserData();
    // Stable string keys for the effects below — `range`/the filter arrays are fresh references
    // on every render (some auth/parent state recreates them even when nothing meaningful
    // changed), so diffing on the objects themselves would refetch on every unrelated re-render
    // instead of just real filter/range changes.
    const rangeKey = "from" in range ? `r:${range.from}:${range.to}` : `w:${range.window}`;
    const categories = categoryFilter.map((c) => c.child);
    const categoriesKey = categories.join(",");
    const companiesKey = companyFilter.join(",");
    const [liveKpis, setLiveKpis] = useState<DashboardKpis | null>(null);
    const [liveCats, setLiveCats] = useState<CategoryRow[] | null>(null);
    const [liveSent, setLiveSent] = useState<SentimentRow[] | null>(null);
    const [callbackRequests, setCallback] = useState<RequestBreakdown>(EMPTY_REQUESTS);
    const [liveChatRequests, setLiveChat] = useState<RequestBreakdown>(EMPTY_REQUESTS);
    const [liveIncidents, setLiveIncidents] = useState<DashboardIncidents | null>(null);
    const [liveIncidentCats, setLiveIncidentCats] = useState<IncidentCategory[] | null>(null);
    const [liveUniqueUsers, setLiveUniqueUsers] = useState<DashboardUniqueUsers | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Latest actual values, read at fetch time — keeps runFetch's own deps down to the stable
    // keys above instead of the ever-changing `range`/`idToken`/array references.
    const latestRef = useRef({ range, idToken, categories, companyFilter, loggedIn });
    latestRef.current = { range, idToken, categories, companyFilter, loggedIn };

    // Guards against out-of-order responses once the fetch is debounced: only the request
    // started most recently may commit state, and never after unmount.
    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);
    useEffect(() => {
        // Reset on setup too (not just rely on the initial useRef value) — React StrictMode's dev
        // double-invoke runs setup -> cleanup -> setup again for the same mount, so without this
        // the cleanup below would leave mountedRef stuck at false even though still mounted.
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    // Key of last params we fetched — null until the first fetch.
    const lastFetchKeyRef = useRef<string | null>(null);

    const runFetch = useCallback(() => {
        const { range, idToken, categories, companyFilter, loggedIn } = latestRef.current;
        if (!loggedIn) return;
        const requestId = ++requestIdRef.current;
        const filters = { categories: categories.length ? categories : undefined, companies: companyFilter.length ? companyFilter : undefined };
        Promise.allSettled([
            fetchKpis(range, idToken, filters),
            fetchCategories(range, idToken, filters),
            fetchSentiment(range, idToken, filters),
            fetchCallbackRequests(range, idToken, filters),
            fetchLiveChatRequests(range, idToken, filters),
            fetchIncidents(range, idToken, filters),
            fetchIncidentCategories(range, idToken, filters),
        ]).then(([k, c, s, cb, lc, inc, incCats]) => {
            if (!mountedRef.current || requestIdRef.current !== requestId) return; // superseded or unmounted
            if (k.status === "fulfilled") setLiveKpis(k.value);
            if (c.status === "fulfilled") setLiveCats(c.value as CategoryRow[]);
            if (s.status === "fulfilled") setLiveSent(s.value as SentimentRow[]);
            if (cb.status === "fulfilled") setCallback(cb.value);
            if (lc.status === "fulfilled") setLiveChat(lc.value);
            if (inc.status === "fulfilled") setLiveIncidents(inc.value);
            if (incCats.status === "fulfilled") setLiveIncidentCats(incCats.value);
            // Treat the core metrics failing (KPIs / categories / sentiment) as an error so the
            // tabs show a clear message instead of a page full of zeros that looks like real data.
            setError(k.status === "rejected" || c.status === "rejected" || s.status === "rejected");
            setLoading(false);
        });
        // loggedIn deliberately excluded from this callback's OWN deps — runFetch reads the live
        // value from latestRef, and auth readiness is handled by the (non-debounced) effect below.
    }, [rangeKey, categoriesKey, companiesKey]);

    const debouncedFetch = useDebouncedCallback(runFetch, FILTER_DEBOUNCE_MS);

    // Single effect for all fetches (immediate first load, then debounced on change) — splitting
    // this into two effects previously double-fired on mount, since both ran in the same commit.
    useEffect(() => {
        if (!loggedIn) return;
        const key = `${rangeKey}|${categoriesKey}|${companiesKey}`;
        if (lastFetchKeyRef.current === key) return; // already fetched/in-flight
        const isFirstLoad = lastFetchKeyRef.current === null;
        lastFetchKeyRef.current = key;
        setLoading(true);
        setError(false);
        if (isFirstLoad) runFetch();
        else debouncedFetch();
    }, [loggedIn, rangeKey, categoriesKey, companiesKey, runFetch, debouncedFetch]);

    // Unique Users is its own fixed day/week/month windows (not the KPI range, and not
    // category filtered, and not debounced) — the only filter it supports is company, so it
    // refetches on companiesKey changes but ignores range/category. Keyed on `loggedIn` (a real,
    // stable boolean) — NOT `idToken` (always a truthy fn reference, so it can't signal auth
    // readiness, and can be a fresh reference on unrelated re-renders, which would otherwise
    // refetch every time).
    useEffect(() => {
        if (!loggedIn) return;
        const { idToken, companyFilter } = latestRef.current;
        let cancelled = false;
        fetchUniqueUsers(idToken, undefined, companyFilter.length ? companyFilter : undefined).then((res) => {
            if (!cancelled) setLiveUniqueUsers(res);
        }).catch(() => {
            if (!cancelled) setLiveUniqueUsers(null);
        });
        return () => {
            cancelled = true;
        };
    }, [loggedIn, companiesKey]);

    // Overall (live when available, else honest ZEROS — never mock/fake data). Already scoped
    // server-side to categoryFilter/companyFilter, so no client-side re-aggregation is needed.
    const mergedKpis = { ...ZERO_KPIS, ...(liveKpis ?? {}) };
    const fullCategories: CategoryRow[] = liveCats ?? [];
    const fullSentiments: SentimentRow[] = liveSent ?? [];
    const allIncidents: LiveIncident[] = liveIncidents?.items ?? [];
    const incPrevTotal = liveIncidents?.prevTotal ?? 0;
    const incPriorComplete = liveIncidents?.priorComplete ?? false;
    const fullIncidentCats: IncidentCategory[] = liveIncidentCats ?? [];

    // Backfill a zero row for any selected category absent from the (already-filtered) response.
    const categoriesOut: CategoryRow[] = categoryFilter.length
        ? categoryFilter.map((sel) => fullCategories.find((c) => c.parent === sel.parent && c.child === sel.child) ?? { category: sel.label, queries: 0, share: 0, parent: sel.parent, child: sel.child })
        : fullCategories;
    const sentimentsOut: SentimentRow[] = categoryFilter.length
        ? categoryFilter.map((sel) => {
              const catLabel = categoriesOut.find((c) => c.parent === sel.parent && c.child === sel.child)?.category ?? sel.label;
              return fullSentiments.find((s) => s.category === catLabel) ?? { category: sel.label, likes: 0, dislikes: 0, positives: [], negatives: [] };
          })
        : fullSentiments;
    const incidentCategoriesOut: IncidentCategory[] = categoryFilter.length
        ? categoryFilter.map((sel) => fullIncidentCats.find((c) => c.parent === sel.parent && c.child === sel.child) ?? { parent: sel.parent, child: sel.child, category: sel.label, incidents: 0, share: 0 })
        : fullIncidentCats;

    const kpis: KpisView = {
        ...mergedKpis,
        // "categories" is the fixed taxonomy size when unfiltered; when the user has picked a
        // subset, show that subset's size instead (matches categoriesOut's row count).
        categories: categoryFilter.length || mergedKpis.categories,
        ...deriveIncidentKpis(allIncidents, incPrevTotal, incPriorComplete),
    };

    return {
        kpis,
        categories: categoriesOut,
        sentiments: sentimentsOut,
        callbackRequests,
        liveChatRequests,
        incidents: allIncidents,
        incidentCategories: incidentCategoriesOut,
        uniqueUsers: liveUniqueUsers,
        loading,
        error,
    };
}
